// Catalogo (redesign 2026-10, mockup "Catalogo B2B goooled").
// Solo l'aspetto è nuovo: prodotti, filtri, ricerca, codice, descrizione della riga, prezzi,
// documenti, zoom e aggiunta al carrello funzionano come prima.
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { urlAsset } from './utils';
import { findCatalogProductId } from './cartImages';
import { FiFileText, FiZap, FiX, FiZoomIn, FiShoppingCart, FiCheck, FiSliders, FiMinus, FiPlus, FiEdit2 } from 'react-icons/fi';

import '../css/CatalogoPage.css';

// Importa il file audio successSound (assicurati che il percorso sia corretto)
import successSound from '../assets/success.mp3';
import { useLanguage } from '../context/LanguageContext';
import {
  localizeCatalogFreeText,
  localizeCatalogOptionValue,
  localizeCatalogProductDescription,
  localizeCatalogProductName,
} from './catalogTextUtils';

const splitList = (value) => (value ? value.split(',').map((item) => item.trim()) : []);

// Le opzioni possono arrivare come testo JSON (prodotti salvati da Gestione prodotti)
const parseOptions = (value) => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch (e) { return []; }
};

const processProduct = (product) => ({
  ...product,
  colore_LED: parseOptions(product.colore_LED),
  ottica: parseOptions(product.ottica),
  finitura: parseOptions(product.finitura),
  power: parseOptions(product.power),
  category: splitList(product.category),
  sottocategoria: splitList(product.sottocategoria),
  ambiente: splitList(product.ambiente),
});

const formatEuro = (value) =>
  `€ ${Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Colore del pallino finitura (dal nome della finitura)
const finishColor = (value) => {
  const s = String(value || '').toLowerCase();
  if (s.includes('bronzo')) return '#8C6A43';
  if (s.includes('oro')) return s.includes('nero') ? 'linear-gradient(135deg,#15181C 50%,#C9A14A 50%)' : 'linear-gradient(135deg,#fff 50%,#C9A14A 50%)';
  if (s.includes('nero') || s.includes('black')) return '#15181C';
  if (s.includes('grigio scuro')) return '#4A4F55';
  if (s.includes('grey') || s.includes('grigio') || s.includes('gray')) return '#9AA1A8';
  if (s.includes('inox') || s.includes('argento') || s.includes('silver')) return 'linear-gradient(135deg,#d9dde1,#9aa3ab)';
  if (s.includes('corten') || s.includes('ruggine')) return '#8B4A2B';
  return '#FFFFFF';
};
// Tinta dell'etichetta temperatura colore: calda / neutra / fredda
const cctTone = (value) => (/2[0-9]00|3000/.test(value) ? 'is-warm' : /5000|5700|6000|6500/.test(value) ? 'is-cool' : '');
const cctDot = (value) => (/2[0-9]00|3000/.test(value) ? '#FFD8A0' : /5000|5700|6000|6500/.test(value) ? '#DDEBFF' : '#FFF4DE');

// Pittogrammi dell'installazione (parete/soffitto come linea spessa)
const mountIcon = (d) => (
  <svg viewBox="0 0 46 30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const MOUNTS = [
  { value: '', key: 'catalog.all', icon: mountIcon(<><rect x="6" y="5" width="14" height="9" rx="1.5" /><rect x="26" y="5" width="14" height="9" rx="1.5" /><rect x="6" y="17" width="14" height="9" rx="1.5" /><rect x="26" y="17" width="14" height="9" rx="1.5" /></>) },
  { value: 'Parete', key: 'catalog.wall', icon: mountIcon(<><path d="M10 2v26" strokeWidth="2.4" /><rect x="11" y="10" width="9" height="10" rx="1.5" /><path d="M22 12l8-3M22 18l8 3" opacity=".55" /></>) },
  { value: 'Soffitto', key: 'catalog.ceiling', icon: mountIcon(<><path d="M4 4h38" strokeWidth="2.4" /><rect x="17" y="5" width="12" height="11" rx="1.5" /><path d="M19 19l-4 7M27 19l4 7" opacity=".55" /></>) },
  { value: 'Sospensione', key: 'catalog.suspension', icon: mountIcon(<><path d="M4 3h38" strokeWidth="2.4" /><path d="M23 3v12" /><path d="M13 15h20v5H13z" /><path d="M17 23l-3 4M29 23l3 4" opacity=".55" /></>) },
  { value: 'Incasso Soffitto', key: 'catalog.recessedCeiling', icon: mountIcon(<><path d="M4 8h12M30 8h12" strokeWidth="2.4" /><path d="M16 4h14v6H16z" /><path d="M19 13l-4 12M27 13l4 12" opacity=".55" /></>) },
  { value: 'Incasso a terra', key: 'catalog.recessedFloor', icon: mountIcon(<><path d="M4 23h12M30 23h12" strokeWidth="2.4" /><path d="M16 21h14v6H16z" /><path d="M19 18l-4-14M27 18l4-14" opacity=".55" /></>) },
  { value: 'Tracklight', key: 'catalog.track', icon: mountIcon(<><path d="M4 5h38" strokeWidth="2.4" /><path d="M4 9h38" strokeWidth="1" /><path d="M14 9v5M30 9v5" /><rect x="10" y="14" width="8" height="7" rx="1.5" /><rect x="26" y="14" width="8" height="7" rx="1.5" /></>) },
];
const ENVIRONMENTS = [
  { value: 'Interno', key: 'catalog.indoor' },
  { value: 'Esterno', key: 'catalog.outdoor' },
];
const SECTORS = [
  { value: 'Residenziale', key: 'catalog.residential' },
  { value: 'Commerciale', key: 'catalog.commercial' },
  { value: 'Industriale', key: 'catalog.industrial' },
  { value: 'Sport', key: 'catalog.sport' },
];
const OPTION_KEYS = ['colore_LED', 'ottica', 'finitura', 'power'];

const matchesFilters = (product, filters) =>
  (!filters.category || product.category.includes(filters.category)) &&
  (!filters.sottocategoria || product.sottocategoria.includes(filters.sottocategoria)) &&
  (!filters.ambiente || product.ambiente.includes(filters.ambiente));

// variant: "catalog" (attivi, non in offerta), "new" (Novità: solo nuovi), "sale" (Offerte: solo in offerta,
// prezzo = prezzo d'offerta come nella vecchia pagina Offerte). Stessa grafica e funzioni per tutte e tre.
const Catalogo = ({ showPrices, searchResults, variant = 'catalog' }) => {
  const onlyNew = variant === 'new';
  const onlySale = variant === 'sale';
  const { addToCart, replaceCartItem } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // tutti, anche fuori da questa pagina (per "Modifica")
  const [editing, setEditing] = useState(null); // riga del carrello aperta con "Modifica"
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    category: '',
    sottocategoria: '',
    ambiente: ''
  });
  const [selections, setSelections] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [bandVisible, setBandVisible] = useState(true);
  const [flashKey, setFlashKey] = useState('');
  const bandRef = useRef(null);
  const topRef = useRef(null);
  const messageTimer = useRef(null);

  const discount = Number(user?.discount || 0);
  // Prezzo netto mostrato: in offerta è il prezzo d'offerta, altrimenti listino meno il tuo sconto
  const netPriceOf = (product) => (onlySale
    ? Number(product.sale_price || product.price || 0)
    : Number(product.price || 0) * (1 - discount / 100));
  const badgePercentOf = (product) => (onlySale
    ? Math.round((1 - netPriceOf(product) / (Number(product.price) || 1)) * 100)
    : discount);

  // Recupera i prodotti dal backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/api/products/public');
        if (response.ok) {
          const data = await response.json();
          const activeProducts = onlyNew
            ? data.filter(product => product.isNew === true && product.active)
            : onlySale
              ? data.filter(product => product.sale === true)
              : data.filter(product => product.active && !product.sale);
          setProducts(activeProducts.map(processProduct));
          setAllProducts(data.map(processProduct));
        } else {
          console.error('Errore nel recupero dei prodotti:', response.statusText);
        }
      } catch (error) {
        console.error('Errore nella richiesta al backend:', error);
      }
    };

    fetchProducts();
  }, [onlyNew, onlySale]);

  // Barra fissa con i filtri attivi quando la fascia dei filtri esce dallo schermo
  useEffect(() => {
    const el = bandRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setBandVisible(entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Esc chiude zoom, scheda prodotto e pannello filtri
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (isZoomed) setIsZoomed(false);
      else if (selectedProduct) closeModal();
      else setFiltersSheetOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isZoomed, selectedProduct]);

  useEffect(() => () => clearTimeout(messageTimer.current), []);

  // Prodotti di partenza: i risultati della ricerca se ci sono, altrimenti tutto il catalogo
  const baseProducts = useMemo(
    () => (searchResults && searchResults.length > 0 ? searchResults.map(processProduct) : products),
    [products, searchResults]
  );
  const filteredProducts = useMemo(
    () => baseProducts.filter((product) => matchesFilters(product, activeFilters)),
    [baseProducts, activeFilters]
  );
  const mountCounts = useMemo(() => {
    const counts = {};
    MOUNTS.forEach((m) => {
      counts[m.value] = baseProducts.filter((p) => matchesFilters(p, { ...activeFilters, sottocategoria: m.value })).length;
    });
    return counts;
  }, [baseProducts, activeFilters]);

  // Funzione per aprire la visualizzazione ingrandita dell'immagine
  const openZoom = () => {
    setIsZoomed(true);
  };

  const closeZoom = () => {
    setIsZoomed(false);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setSelectedImage(product.image);
    setQuantity(1);
  };

  function closeModal() {
    setSelectedProduct(null);
    setSelectedImage(null);
    if (editing) cancelEdit();
  }

  // "Modifica" dal carrello: riapre la scheda del prodotto con le scelte e la quantità della riga.
  // Le scelte si ricavano dal codice "<id>-<colore>-<ottica>-<finitura>-<potenza>".
  useEffect(() => {
    const item = location.state?.editItem;
    if (!item || allProducts.length === 0) return;
    navigate(location.pathname, { replace: true, state: null });
    const ids = allProducts.map((p) => String(p.id)).sort((a, b) => b.length - a.length);
    const productId = findCatalogProductId(item.code, ids);
    const product = allProducts.find((p) => String(p.id) === productId);
    if (!product) {
      showMessage(t('catalog.editNotFound'));
      return;
    }
    const parts = String(item.code).slice(productId.length + 1).split('-');
    const chosen = {};
    OPTION_KEYS.forEach((key, i) => {
      const opt = (product[key] || []).find((o) => String(o.code) === parts[i]);
      if (opt) chosen[key] = opt;
    });
    setSelections((prev) => ({ ...prev, [product.id]: chosen }));
    setSelectedProduct(product);
    setSelectedImage(product.image);
    setQuantity(Math.max(1, parseInt(item.quantity, 10) || 1));
    setEditing(item);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts, location.state]);

  const cancelEdit = () => {
    setEditing(null);
    navigate('/cart');
  };

  // Gestisci i filtri attivi (uno per gruppo; ricliccando si toglie)
  const handleFilterChange = (filterType, filterValue) => {
    let newFilters = { ...activeFilters };
    if (newFilters[filterType] === filterValue) {
      newFilters[filterType] = '';
    } else {
      newFilters[filterType] = filterValue;
    }
    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setActiveFilters({
      category: '',
      sottocategoria: '',
      ambiente: ''
    });
  };

  const handleSelectionChange = (productId, type, value) => {
    setSelections(prevSelections => ({
      ...prevSelections,
      [productId]: {
        ...prevSelections[productId],
        [type]: value
      }
    }));
    setFlashKey(type);
    setTimeout(() => setFlashKey(''), 600);
  };

  // Funzione per generare il codice prodotto
  const generateProductCode = (product) => {
    const selected = selections[product.id] || {};
    const colorCode = selected.colore_LED?.code || product.colore_LED?.[0]?.code || '';
    const otticaCode = selected.ottica?.code || product.ottica?.[0]?.code || '';
    const finituraCode = selected.finitura?.code || product.finitura?.[0]?.code || '';
    const powerCode = selected.power?.code || product.power?.[0]?.code || '';
    return `${product.id}-${colorCode}-${otticaCode}-${finituraCode}-${powerCode}`;
  };

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play();
  };

  // Riga del carrello per il prodotto con le scelte correnti
  const buildCartRow = (product, quantity) => {
    const selected = selections[product.id] || {};
    const productName = localizeCatalogProductName(product, lang);

    const description = `
      ${productName}
      ${selected.colore_LED ? `${t('catalog.ledColor')}: ${localizeCatalogOptionValue(selected.colore_LED.value, lang)}` : product.colore_LED?.[0]?.value ? `${t('catalog.ledColor')}: ${localizeCatalogOptionValue(product.colore_LED[0].value, lang)}` : ''}
      ${selected.ottica ? `${t('catalog.optics')}: ${localizeCatalogOptionValue(selected.ottica.value, lang)}` : product.ottica?.[0]?.value ? `${t('catalog.optics')}: ${localizeCatalogOptionValue(product.ottica[0].value, lang)}` : ''}
      ${selected.finitura ? `${t('catalog.finish')}: ${localizeCatalogOptionValue(selected.finitura.value, lang)}` : product.finitura?.[0]?.value ? `${t('catalog.finish')}: ${localizeCatalogOptionValue(product.finitura[0].value, lang)}` : ''}
      ${selected.power ? `${t('catalog.power')}: ${localizeCatalogOptionValue(selected.power.value, lang)}` : product.power?.[0]?.value ? `${t('catalog.power')}: ${localizeCatalogOptionValue(product.power[0].value, lang)}` : ''}
      ${product.input_voltage ? `${t('catalog.voltage')}: ${product.input_voltage}` : ''}

    `.trim();

    const item = {
      code: generateProductCode(product),
      description: description.replace(/\n\s+/g, '\n'),
      unitPrice: onlySale ? (product.sale_price || product.price) : product.price,
      quantity: parseInt(quantity, 10),
      datasheet: product.datasheet || null,   // 👈 AGGIUNTO QUI
    };
    return item;
  };

  const handleAddToCart = (product, quantity) => {
    const productName = localizeCatalogProductName(product, lang);
    const item = buildCartRow(product, quantity);
    // In modifica: la riga resta al suo posto con prezzo, sconto, zona e note; cambiano scelte e quantità.
    if (editing) {
      const { code, description, datasheet } = item;
      replaceCartItem(editing.id, { code, description, datasheet, quantity: item.quantity });
      setEditing(null);
      navigate('/cart', { state: { updatedId: editing.id } });
      return;
    }

    addToCart(item);
    playSound();
    showMessage(`${t('catalog.addedToCart')} · ${productName} × ${parseInt(quantity, 10)}`);
  };

  const showMessage = (text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(''), 2400);
  };

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;
    // urlAsset punta a goooled.it (dominio diverso da b2b.goooled.com): l'attributo
    // download di un <a> viene ignorato cross-origin, quindi apriamo in una nuova
    // scheda per non far navigare via il B2B dalla pagina corrente.
    window.open(urlAsset(fileUrl), '_blank', 'noopener,noreferrer');
  };

  // Scheda tecnica: se il PDF esiste già online lo apre, altrimenti il backend lo genera al volo.
  // Apro la finestra subito (sincrono, nel click) per non farla bloccare dal popup blocker,
  // poi la reindirizzo: così il PDF si apre sempre in una tab separata e mai al posto del catalogo.
  const handleDatasheetDownload = (product) => {
    const newWindow = window.open('', '_blank');
    const apiUrl = `/api/products/${encodeURIComponent(product.id)}/datasheet`;
    if (newWindow) {
      newWindow.location.href = apiUrl;
    } else {
      window.open(apiUrl, '_blank');
    }
  };

  const selectedProductName = selectedProduct
    ? localizeCatalogProductName(selectedProduct, lang)
    : '';
  const selectedProductDescription = selectedProduct
    ? localizeCatalogProductDescription(selectedProduct, lang)
    : '';

  const optionLabel = (key, value) =>
    key === 'power' ? localizeCatalogFreeText(value, lang) : localizeCatalogOptionValue(value, lang);
  const optionTitle = { colore_LED: t('catalog.ledColor'), ottica: t('catalog.optics'), finitura: t('catalog.finish'), power: t('catalog.power') };

  const hasFilters = !!(activeFilters.category || activeFilters.sottocategoria || activeFilters.ambiente);
  const sheetFilterCount = [activeFilters.category, activeFilters.ambiente].filter(Boolean).length;
  const activeChips = [
    activeFilters.sottocategoria && { type: 'sottocategoria', label: t(MOUNTS.find((m) => m.value === activeFilters.sottocategoria)?.key || '') },
    activeFilters.ambiente && { type: 'ambiente', label: t(ENVIRONMENTS.find((m) => m.value === activeFilters.ambiente)?.key || '') },
    activeFilters.category && { type: 'category', label: t(SECTORS.find((m) => m.value === activeFilters.category)?.key || '') },
  ].filter(Boolean);

  const renderChipGroup = (title, type, list) => (
    <div className="cat-fgrp">
      <b>{title}</b>
      <div className="cat-chips">
        {list.map((o) => (
          <button
            key={o.value}
            type="button"
            className="cat-chip"
            aria-pressed={activeFilters[type] === o.value}
            onClick={() => handleFilterChange(type, o.value)}
          >
            {t(o.key)}
          </button>
        ))}
      </div>
    </div>
  );

  const productCode = selectedProduct ? generateProductCode(selectedProduct).split('-') : [];
  // Il codice base può contenere "-" (es. "Telemaco pendente-230"): le ultime 4 parti sono le opzioni
  const codeBase = productCode.slice(0, Math.max(1, productCode.length - 4)).join('-');
  const codeParts = productCode.slice(-4);

  const galleryImages = selectedProduct
    ? [
      [selectedProduct.image, t('catalog.photo')],
      [selectedProduct.image2, `${t('catalog.photo')} 2`],
      [selectedProduct.image3, `${t('catalog.photo')} 3`],
      [selectedProduct.image4, `${t('catalog.photo')} 4`],
      [selectedProduct.techDrawing, t('catalog.drawing')],
    ].filter(([src]) => !!src)
    : [];

  return (
    <div className="cat">
      <div ref={topRef} />
      {/* Fascia con titolo e filtri */}
      <section className="cat-band" ref={bandRef}>
        <div className="cat-band-in">
          <div className="cat-head">
            <div>
              <h1>{onlyNew ? t('home.newArrivalsTitle') : onlySale ? t('home.onSaleTitle') : t('catalog.pageTitle')}</h1>
              <p>{onlyNew ? t('home.newArrivalsSubtitle') : onlySale ? t('home.onSaleSubtitle') : t('catalog.pageSubtitle')}</p>
            </div>
            <span className="cat-count">
              {t('catalog.countOf', { n: filteredProducts.length, total: baseProducts.length })}
            </span>
          </div>

          <div className="cat-mounts" role="group" aria-label={t('catalog.installation')}>
            {MOUNTS.map((m) => (
              <button
                key={m.value || 'all'}
                type="button"
                className="cat-mt"
                aria-pressed={activeFilters.sottocategoria === m.value}
                onClick={() => (m.value ? handleFilterChange('sottocategoria', m.value) : setActiveFilters((f) => ({ ...f, sottocategoria: '' })))}
              >
                {m.icon}
                <span>{t(m.key)}</span>
                <small>{mountCounts[m.value]}</small>
              </button>
            ))}
          </div>

          <div className="cat-fbar">
            {renderChipGroup(t('catalog.environment'), 'ambiente', ENVIRONMENTS)}
            {renderChipGroup(t('catalog.sector'), 'category', SECTORS)}
            <button type="button" className="cat-reset" onClick={resetFilters} disabled={!hasFilters}>
              {t('catalog.resetAll')}
            </button>
          </div>

          <div className="cat-mfilters">
            <button type="button" className="cat-mbtn" onClick={() => setFiltersSheetOpen(true)}>
              <FiSliders aria-hidden="true" /> {t('catalog.filters')}
              {sheetFilterCount > 0 && <span className="cat-n">{sheetFilterCount}</span>}
            </button>
            {hasFilters && (
              <button type="button" className="cat-reset" onClick={resetFilters}>{t('catalog.reset')}</button>
            )}
          </div>
        </div>
      </section>

      {/* Barra fissa con i filtri attivi, quando si scorre */}
      {!bandVisible && (
        <div className="cat-sticky">
          <span className="cat-count">{t('catalog.countOf', { n: filteredProducts.length, total: baseProducts.length })}</span>
          <div className="cat-sticky-chips">
            {activeChips.map((c) => (
              <button key={c.type} type="button" className="cat-chip" aria-pressed="true" onClick={() => handleFilterChange(c.type, activeFilters[c.type])}>
                {c.label} <FiX aria-hidden="true" />
              </button>
            ))}
          </div>
          <button type="button" className="cat-reset" onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            {t('catalog.editFilters')}
          </button>
        </div>
      )}

      <section className="cat-grid-wrap">
        {filteredProducts.length > 0 ? (
          <div className="cat-grid">
            {filteredProducts.map(product => {
              const prezzoNetto = netPriceOf(product);
              const productName = localizeCatalogProductName(product, lang);
              const specs = [product.input_voltage, product.ipRating ? `IP${product.ipRating}` : '', product.lumen].filter(Boolean).join(' · ');

              return (
                <button key={product.id} type="button" className="cat-card" onClick={() => handleProductClick(product)}>
                  <span className="cat-photo">
                    <img src={urlAsset(product.image)} alt={productName} loading="lazy" />
                    {product.madeInItaly && (
                      <span className="cat-mii"><i aria-hidden="true" />{t('catalog.madeInItaly')}</span>
                    )}
                    {onlySale && badgePercentOf(product) > 0 && (
                      <span className="cat-salebadge">−{badgePercentOf(product)}%</span>
                    )}
                  </span>
                  <span className="cat-cbody">
                    <span className="cat-name">{productName}</span>
                    {showPrices && (
                      <span className="cat-net">
                        <s className="cat-net-list" title={t('catalog.listPrice')}>{formatEuro(product.price)}</s>
                        {formatEuro(prezzoNetto)}<small>{t('catalog.netShort')}</small>
                      </span>
                    )}
                    {specs && <span className="cat-specs">{specs}</span>}
                    <span className="cat-opts">
                      {(product.finitura || []).map((o) => (
                        <span key={o.code} className="cat-sw" title={localizeCatalogOptionValue(o.value, lang)} style={{ background: finishColor(o.value) }} />
                      ))}
                      {(product.finitura || []).length > 0 && (product.colore_LED || []).length > 0 && <span className="cat-sep" />}
                      {(product.colore_LED || []).map((o) => (
                        <span key={o.code} className={`cat-cct ${cctTone(o.value)}`}>{localizeCatalogOptionValue(o.value, lang)}</span>
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="cat-empty">
            <p>{t('catalog.noProducts')}</p>
            {hasFilters && <button type="button" className="cat-chip" onClick={resetFilters}>{t('catalog.resetAll')}</button>}
          </div>
        )}
      </section>

      {/* Scheda prodotto */}
      {selectedProduct && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="cat-modal" onClick={closeModal}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="cat-sheet" role="dialog" aria-modal="true" aria-label={selectedProductName} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cat-close" onClick={closeModal} aria-label={t('catalog.close')}>
              <FiX aria-hidden="true" />
            </button>

            <div className="cat-gal">
              <button type="button" className="cat-hero" onClick={openZoom} aria-label={t('catalog.zoom')}>
                <img src={urlAsset(selectedImage)} alt={selectedProductName} />
                {selectedProduct.madeInItaly && (
                  <span className="cat-mii"><i aria-hidden="true" />{t('catalog.madeInItaly')}</span>
                )}
                <span className="cat-zoomhint"><FiZoomIn aria-hidden="true" /> {t('catalog.zoom')}</span>
              </button>
              <div className="cat-thumbs">
                {galleryImages.map(([src, label]) => (
                  <button key={src + label} type="button" className="cat-th" aria-pressed={src === selectedImage} onClick={() => handleImageClick(src)}>
                    <img src={urlAsset(src)} alt={`${selectedProductName} - ${label}`} />
                    <em>{label}</em>
                  </button>
                ))}
              </div>
              {(selectedProduct.datasheet || selectedProduct.fotometria) && (
                <div className="cat-docs">
                  {selectedProduct.datasheet && (
                    <button type="button" className="cat-doc" onClick={() => handleDatasheetDownload(selectedProduct)}>
                      <FiFileText aria-hidden="true" /> {t('catalog.datasheet')} <small>PDF</small>
                    </button>
                  )}
                  {selectedProduct.fotometria && (
                    <button type="button" className="cat-doc" onClick={() => handleDownload(selectedProduct.fotometria)}>
                      <FiZap aria-hidden="true" /> {t('catalog.photometry')} <small>LDT</small>
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="cat-conf">
              <div className="cat-cscroll">
                {editing && (
                  <div className="cat-editbar">
                    <FiEdit2 aria-hidden="true" />
                    <span><b>{t('catalog.editingRow')}</b> · <code>{editing.code}</code></span>
                    <button type="button" onClick={cancelEdit}>{t('catalog.cancelEdit')}</button>
                  </div>
                )}
                <div className="cat-ctitle">
                  <div className="cat-crumb">
                    {[selectedProduct.sottocategoria.join(' · '), selectedProduct.ambiente.join(' / ')].filter(Boolean).join(' — ')}
                  </div>
                  <h2>{selectedProductName}</h2>
                  {selectedProductDescription && <p>{selectedProductDescription}</p>}
                </div>

                <div className="cat-codebox">
                  <span>{t('catalog.code')}</span>
                  <code>
                    {codeBase}
                    {codeParts.map((part, i) => (
                      <React.Fragment key={OPTION_KEYS[i]}>
                        -<b className={flashKey === OPTION_KEYS[i] ? 'is-flash' : ''}>{part}</b>
                      </React.Fragment>
                    ))}
                  </code>
                </div>

                {OPTION_KEYS.filter((key) => selectedProduct[key] && selectedProduct[key].length > 0).map((key) => {
                  const opts = selectedProduct[key];
                  const chosen = selections[selectedProduct.id]?.[key] || opts[0];
                  if (opts.length === 1) {
                    return (
                      <div key={key} className="cat-optrow">
                        <div><b>{optionTitle[key]}</b></div>
                        <div className="cat-fixed">{optionLabel(key, opts[0].value)}<small>{t('catalog.onlyVersion')}</small></div>
                      </div>
                    );
                  }
                  return (
                    <div key={key} className="cat-optrow">
                      <div><b>{optionTitle[key]}</b><span>{optionLabel(key, chosen.value)}</span></div>
                      <div className="cat-segs" role="radiogroup" aria-label={optionTitle[key]}>
                        {opts.map((opt) => (
                          <button
                            key={opt.code}
                            type="button"
                            role="radio"
                            className="cat-sg"
                            aria-checked={opt.code === chosen.code}
                            onClick={() => handleSelectionChange(selectedProduct.id, key, opt)}
                          >
                            {key === 'finitura' && <span className="cat-k" style={{ background: finishColor(opt.value) }} />}
                            {key === 'colore_LED' && <span className="cat-k" style={{ background: cctDot(opt.value) }} />}
                            {optionLabel(key, opt.value)}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="cat-tech">
                  <div><span>{t('catalog.voltage')}</span><strong>{selectedProduct.input_voltage || '—'}</strong></div>
                  <div><span>IP</span><strong>{selectedProduct.ipRating ? `IP${selectedProduct.ipRating}` : '—'}</strong></div>
                  <div><span>{t('catalog.lumen')}</span><strong>{selectedProduct.lumen || '—'}</strong></div>
                  <div><span>{t('catalog.size')}</span><strong>{selectedProduct.size ? localizeCatalogFreeText(selectedProduct.size, lang) : '—'}</strong></div>
                </div>
              </div>

              <div className="cat-buy">
                {showPrices ? (
                  <div className="cat-prices">
                    <span className="cat-list">{t('catalog.listPrice')} <s>{formatEuro(selectedProduct.price)}</s></span>
                    <span className="cat-pnet">
                      {formatEuro(netPriceOf(selectedProduct))}
                      {badgePercentOf(selectedProduct) > 0 && <small>−{badgePercentOf(selectedProduct)}%</small>}
                    </span>
                  </div>
                ) : <div />}
                <div className="cat-add">
                  <div className="cat-qty">
                    <button type="button" onClick={() => setQuantity((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))} aria-label="−"><FiMinus aria-hidden="true" /></button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onBlur={() => setQuantity((q) => Math.max(1, parseInt(q, 10) || 1))}
                      aria-label={t('catalog.qtyAria')}
                    />
                    <button type="button" onClick={() => setQuantity((q) => (parseInt(q, 10) || 0) + 1)} aria-label="+"><FiPlus aria-hidden="true" /></button>
                  </div>
                  <button
                    type="button"
                    className="cat-cta"
                    onClick={() => handleAddToCart(selectedProduct, Math.max(1, parseInt(quantity, 10) || 1))}
                  >
                    {editing
                      ? <><FiCheck aria-hidden="true" /> {t('catalog.updateRow')}</>
                      : <><FiShoppingCart aria-hidden="true" /> {t('catalog.addToCart')}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoom dell'immagine */}
      {isZoomed && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="cat-zoom" onClick={closeZoom}>
          <img
            src={urlAsset(selectedImage)}
            alt={selectedProduct ? `${t('catalog.zoomAlt')} ${localizeCatalogProductName(selectedProduct, lang)}` : t('catalog.zoomAlt')}
          />
        </div>
      )}

      {/* Filtri su telefono */}
      {filtersSheetOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="cat-fsheet" onClick={() => setFiltersSheetOpen(false)}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="cat-fpanel" onClick={(e) => e.stopPropagation()}>
            <h3>
              {t('catalog.filters')}
              <button type="button" className="cat-reset" onClick={resetFilters} disabled={!hasFilters}>{t('catalog.reset')}</button>
            </h3>
            {renderChipGroup(t('catalog.environment'), 'ambiente', ENVIRONMENTS)}
            {renderChipGroup(t('catalog.sector'), 'category', SECTORS)}
            <button type="button" className="cat-cta" onClick={() => setFiltersSheetOpen(false)}>
              {t('catalog.showProducts', { n: filteredProducts.length })}
            </button>
          </div>
        </div>
      )}

      {/* Messaggio "aggiunto al carrello" */}
      <div className={`cat-toast${message ? ' is-on' : ''}`} role="status" aria-live="polite">
        {message && (
          <>
            <i aria-hidden="true"><FiCheck /></i>
            <span>{message}</span>
            <button type="button" onClick={() => navigate('/cart')}>{t('catalog.goToCart')}</button>
          </>
        )}
      </div>
    </div>
  );
};

export default Catalogo;
